import type { ApiClient } from '../client'
import type {
  Appointment,
  AppointmentEvent,
  AvailabilityMeta,
  AvailabilityQuery,
  AvailabilitySlot,
  CreateAppointmentRequest,
  CreateWaitlistRequest,
  ListAppointmentsQuery,
  ListWaitlistQuery,
  RescheduleRequest,
  TransitionRequest,
  TransitionResponse,
  UpdateWaitlistRequest,
  WaitlistEntry,
} from '../types'

/** src/modules/appointments/controllers/appointments.controller.ts */
export function appointmentsEndpoints(api: ApiClient) {
  return {
    list: (query: ListAppointmentsQuery) =>
      api.get<readonly Appointment[]>('appointments', { query: { ...query } }),
    get: (id: string) => api.get<Appointment>(`appointments/${id}`),
    events: (id: string) =>
      api.get<readonly AppointmentEvent[]>(`appointments/${id}/events`, { query: { limit: 200 } }),
    create: (body: CreateAppointmentRequest) => api.post<Appointment>('appointments', body),
    reschedule: (id: string, body: RescheduleRequest) =>
      api.patch<Appointment>(`appointments/${id}`, body),
    /** The state-machine endpoint; §6.1 rules are enforced by the service. */
    transition: (id: string, body: TransitionRequest) =>
      api.patch<TransitionResponse>(`appointments/${id}/status`, body),
  }
}

/** src/modules/availability/controllers/availability.controller.ts — `{ data: slots, meta: { timezone, calendar_source } }`. */
export function availabilityEndpoints(api: ApiClient) {
  return {
    query: async (query: AvailabilityQuery) => {
      const result = await api.request<readonly AvailabilitySlot[]>('availability', {
        query: { ...query },
      })
      return { slots: result.data, meta: result.meta as unknown as AvailabilityMeta }
    },
  }
}

/** src/modules/waitlist/controllers/waitlist.controller.ts — cursor-paginated list. */
export function waitlistEndpoints(api: ApiClient) {
  return {
    list: (query: ListWaitlistQuery = {}) =>
      api.getPage<WaitlistEntry>('waitlist', { query: { ...query } }),
    get: (id: string) => api.get<WaitlistEntry>(`waitlist/${id}`),
    create: (body: CreateWaitlistRequest) => api.post<WaitlistEntry>('waitlist', body),
    update: (id: string, body: UpdateWaitlistRequest) =>
      api.patch<WaitlistEntry>(`waitlist/${id}`, body),
    deactivate: (id: string) => api.post<WaitlistEntry>(`waitlist/${id}/deactivate`),
  }
}
